// useCanvasStream — shared per-token canvas streaming subscriber for the
// AI Chat "Write to canvas" mode. Consumed by both DraftCanvas (active draft
// exists) and BlankCanvas (no draft yet).
//
// Lifecycle:
//   1. `draft:stream {start}` → onStart() callback prepares a target Draft
//      revision (creating Piece + Draft for the blank case) and locks the
//      canvas.
//   2. `draft:stream {chunk, markdown}` → re-runs markdownToTiptap and feeds
//      it into the editor via BriefEditor's `streamContent` prop. We also
//      mirror the doc into local state so an unmount mid-stream can still
//      flush it on end.
//   3. `draft:stream {end, markdown, aborted}` → onEnd() saves the final doc
//      to the target Draft revision and unlocks the canvas.

import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { composerBus, type DraftStreamMode } from "@/lib/composerBus";
import { markdownToTiptapDoc } from "@/lib/markdownToTiptap";

export type StreamTarget = {
  /** The Draft (`content_piece_drafts.id`) the final doc must be saved into. */
  draftId: string;
  /** Workspace + brief context for the BriefEditor render. */
  workspaceId: string;
  briefId: string;
  /** Human label, used for the auto-stash + final save title. */
  label: string;
  /**
   * When set (append mode), each incoming chunk is rendered as
   * `{ type:"doc", content:[...seedContent, ...parsedChunk.content] }`
   * so the streamed content appears after the existing draft body.
   */
  seedContent?: unknown[];
  /** Chosen mode; useful for downstream save behavior. Defaults to "new". */
  mode?: DraftStreamMode;
};

export type UseCanvasStreamArgs = {
  enabled: boolean;
  /**
   * Called when a `start` event arrives. Should:
   *   - stash any dirty work as a Draft (if not already saved),
   *   - create the new target Draft revision (or Piece+Draft),
   *   - return the target meta so chunks can render.
   * Throwing aborts the stream (we toast + ignore subsequent chunks).
   */
  onStart: (payload: { label: string; mode: DraftStreamMode }) => Promise<StreamTarget>;
  /**
   * Called on `end` with the final TipTap doc. Should save into the target
   * and refresh the parent (e.g. via onDraftPromoted).
   */
  onEnd: (args: {
    target: StreamTarget;
    doc: unknown;
    aborted: boolean;
  }) => Promise<void>;
};

export type CanvasStreamState = {
  active: boolean;
  /**
   * True once the stream has ended and its final doc is saved, but the
   * parent's `latestDraft` prop hasn't caught up yet. The canvas keeps
   * rendering `doc` (locked) until `acknowledge()` is called so users
   * don't see the pre-append body flicker back.
   */
  settling: boolean;
  target: StreamTarget | null;
  doc: unknown;
};

export type UseCanvasStreamReturn = CanvasStreamState & {
  /** Clear a settling stream — call once parent props reflect the saved doc. */
  acknowledge: () => void;
};


export function useCanvasStream({ enabled, onStart, onEnd }: UseCanvasStreamArgs): UseCanvasStreamReturn {
  const [state, setState] = useState<CanvasStreamState>({
    active: false,
    settling: false,
    target: null,
    doc: null,
  });
  const targetRef = useRef<StreamTarget | null>(null);
  const docRef = useRef<unknown>(null);
  const lastGoodDocRef = useRef<unknown>(null);
  const startPromiseRef = useRef<Promise<StreamTarget | null> | null>(null);
  const pendingMarkdownRef = useRef<string | null>(null);

  const reset = useCallback(() => {
    targetRef.current = null;
    docRef.current = null;
    lastGoodDocRef.current = null;
    startPromiseRef.current = null;
    pendingMarkdownRef.current = null;
    setState({ active: false, settling: false, target: null, doc: null });
  }, []);

  const acknowledge = useCallback(() => {
    // Called by the parent once its refreshed `latestDraft` reflects the
    // just-saved doc, so we can hand rendering back to DraftBodyView.
    reset();
  }, [reset]);

  const renderChunk = useCallback((markdown: string) => {
    const target = targetRef.current;
    if (!target) return;
    const seed = target.seedContent ?? [];
    let doc: unknown;
    try {
      const parsed = markdownToTiptapDoc(markdown) as {
        type: string;
        content?: unknown[];
      };
      if (seed.length > 0) {
        doc = { type: "doc", content: [...seed, ...(parsed.content ?? [])] };
      } else {
        doc = parsed;
      }
      lastGoodDocRef.current = doc;
    } catch (err) {
      if (import.meta.env.DEV) {
        console.warn("useCanvasStream: markdown parse failed on chunk", err);
      }
      if (lastGoodDocRef.current) {
        doc = lastGoodDocRef.current;
      } else {
        const paragraph = {
          type: "paragraph",
          content: [{ type: "text", text: markdown }],
        };
        doc = {
          type: "doc",
          content: seed.length > 0 ? [...seed, paragraph] : [paragraph],
        };
      }
    }
    docRef.current = doc;
    setState((prev) => (prev.active ? { ...prev, doc } : prev));
  }, []);

  useEffect(() => {
    if (!enabled) return;
    return composerBus.subscribe("draft:stream", (payload) => {
      if (payload.phase === "start") {
        // A new stream cancels any settling doc from a prior turn.
        reset();
        const mode: DraftStreamMode = payload.mode ?? "new";
        startPromiseRef.current = (async () => {
          try {
            const target = await onStart({
              label: payload.label ?? "Excerptus AI draft",
              mode,
            });
            targetRef.current = target;
            const seed = target.seedContent ?? [];
            docRef.current = {
              type: "doc",
              content: seed.length > 0 ? [...seed, { type: "paragraph" }] : [{ type: "paragraph" }],
            };
            setState({
              active: true,
              settling: false,
              target,
              doc: docRef.current,
            });
            if (pendingMarkdownRef.current != null) {
              const pending = pendingMarkdownRef.current;
              pendingMarkdownRef.current = null;
              renderChunk(pending);
            }
            return target;
          } catch (err) {
            toast.error((err as Error).message || "Couldn't prepare canvas for streaming");
            reset();
            return null;
          }
        })();
        return;
      }
      if (payload.phase === "chunk") {
        if (!targetRef.current) {
          pendingMarkdownRef.current = payload.markdown;
          return;
        }
        renderChunk(payload.markdown);
        return;
      }

      if (payload.phase === "end") {
        void (async () => {
          const target = await (startPromiseRef.current ?? Promise.resolve(targetRef.current));
          if (!target) {
            reset();
            return;
          }
          const seed = target.seedContent ?? [];
          let doc: unknown;
          try {
            const parsed = markdownToTiptapDoc(payload.markdown || "") as {
              type: string;
              content?: unknown[];
            };
            if (seed.length > 0) {
              doc = { type: "doc", content: [...seed, ...(parsed.content ?? [])] };
            } else {
              doc = parsed;
            }
          } catch {
            const paragraph = {
              type: "paragraph",
              content: [{ type: "text", text: payload.markdown ?? "" }],
            };
            doc = {
              type: "doc",
              content: seed.length > 0 ? [...seed, paragraph] : [paragraph],
            };
          }
          try {
            await onEnd({ target, doc, aborted: Boolean(payload.aborted) });
            if (payload.aborted) {
              toast.message("Stream interrupted — partial draft saved", {
                description: "Find it in the Drafts tab.",
              });
              reset();
              return;
            }
            // Save succeeded — enter "settling": keep the streamed doc on
            // screen (locked) until the parent's refreshed latestDraft reflects
            // it and calls acknowledge(). This avoids the flicker back to the
            // pre-append body while the parent refetch is in flight.
            docRef.current = doc;
            setState({ active: true, settling: true, target, doc });
          } catch (err) {
            toast.error((err as Error).message || "Saving the streamed draft failed");
            reset();
          }
        })();
      }
    });
  }, [enabled, onStart, onEnd, reset, renderChunk]);

  return { ...state, acknowledge };
}




